import { useTranslation } from "react-i18next";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { motion } from "framer-motion";
import {
  CheckCircle,
  ArrowRight,
  Sparkles,
  Heart,
  Clock,
  Star,
  BookOpen,
  Target,
  Users,
  Brain,
  Award,
  FileText,
  CheckSquare,
  AlertCircle,
  Edit3,
  GripVertical,
  Eye,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import React, { useState, useRef } from "react";
import {
  UbdMaarifPlannerResponseDtoCourseType,
  LearningOutcomeDto,
} from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";

interface UbdPlanData {
  courseType: UbdMaarifPlannerResponseDtoCourseType | "";
  grade: number;
  learningOutcomes: LearningOutcomeDto[];
  ubdUnderstandingGoals: string[];
  ubdTransferGoals: string[];
  ubdEssentialQuestions: string[];
  ubdPerformanceTasks: string[];
  ubdOtherEvidence: string[];
  planId?: number;
  // TYMM Data
  temelKabuller?: string;
  onDegerlendirme?: string;
  koprukurma?: string;
  zenginlestirme?: string;
  destekleme?: string;
  ogrenmeOgretmeUygulamalari?: string[];
}

interface LessonCard {
  id: string;
  code: string;
  title: string;
  content: string;
  type:
    | "transfer"
    | "understanding"
    | "questions"
    | "performance"
    | "evidence"
    | "tymm";
  color: string;
  icon: React.ElementType;
  editable: boolean;
  order: number;
}

interface UbdPlanSummaryStepProps {
  formData: UbdPlanData;
  isLoading: boolean;
  onCreateLessonFlow: () => void;
}

const UbdPlanSummaryStep: React.FC<UbdPlanSummaryStepProps> = ({
  formData,
  isLoading,
  onCreateLessonFlow,
}) => {
  const { t } = useTranslation();
  const [cards, setCards] = useState<LessonCard[]>([]);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>("");

  // Convert formData to LessonCard format
  const generateCards = (): LessonCard[] => {
    const lessonCards: LessonCard[] = [];
    let order = 0;

    // Transfer Goals
    formData.ubdTransferGoals.forEach((goal, index) => {
      lessonCards.push({
        id: `transfer-${index}`,
        code: `T${index + 1}`,
        title: "Transfer Hedefi",
        content: goal,
        type: "transfer",
        color: "purple",
        icon: Target,
        editable: true,
        order: order++,
      });
    });

    // Understanding Goals
    formData.ubdUnderstandingGoals.forEach((goal, index) => {
      lessonCards.push({
        id: `understanding-${index}`,
        code: `A${index + 1}`,
        title: "Anlama Hedefi",
        content: goal,
        type: "understanding",
        color: "amber",
        icon: Brain,
        editable: true,
        order: order++,
      });
    });

    // Essential Questions
    formData.ubdEssentialQuestions.forEach((question, index) => {
      lessonCards.push({
        id: `questions-${index}`,
        code: `ÖS${index + 1}`,
        title: "Önemli Soru",
        content: question,
        type: "questions",
        color: "blue",
        icon: FileText,
        editable: true,
        order: order++,
      });
    });

    // Performance Tasks
    formData.ubdPerformanceTasks.forEach((task, index) => {
      lessonCards.push({
        id: `performance-${index}`,
        code: `PG${index + 1}`,
        title: "Performans Görevi",
        content: task,
        type: "performance",
        color: "emerald",
        icon: Award,
        editable: true,
        order: order++,
      });
    });

    // Other Evidence
    formData.ubdOtherEvidence.forEach((evidence, index) => {
      lessonCards.push({
        id: `evidence-${index}`,
        code: `DK${index + 1}`,
        title: "Diğer Kanıt",
        content: evidence,
        type: "evidence",
        color: "teal",
        icon: CheckCircle,
        editable: true,
        order: order++,
      });
    });

    // TYMM Learning-Teaching Applications
    if (
      formData.ogrenmeOgretmeUygulamalari &&
      formData.ogrenmeOgretmeUygulamalari.length > 0
    ) {
      formData.ogrenmeOgretmeUygulamalari.forEach((app, index) => {
        lessonCards.push({
          id: `tymm-${index}`,
          code: `E${index + 1}`,
          title: "Öğrenme-Öğretme Uygulaması",
          content: app,
          type: "tymm",
          color: "indigo",
          icon: BookOpen,
          editable: true,
          order: order++,
        });
      });
    }

    return lessonCards.sort((a, b) => a.order - b.order);
  };

  // Initialize cards when component mounts or formData changes
  React.useEffect(() => {
    setCards(generateCards());
  }, [formData]);

  // Edit functions
  const handleStartEdit = (card: LessonCard) => {
    setEditingCard(card.id);
    setEditContent(card.content);
  };

  const handleSaveEdit = () => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === editingCard ? { ...card, content: editContent } : card,
      ),
    );
    setEditingCard(null);
    setEditContent("");
  };

  const handleCancelEdit = () => {
    setEditingCard(null);
    setEditContent("");
  };

  // Group cards by type
  const groupedCards = cards.reduce(
    (acc, card) => {
      if (!acc[card.type]) {
        acc[card.type] = [];
      }
      acc[card.type].push(card);
      return acc;
    },
    {} as Record<string, LessonCard[]>,
  );

  const getGroupTitle = (type: string) => {
    const titles = {
      transfer: "Transfer Hedefleri",
      understanding: "Anlama Hedefleri",
      questions: "Önemli Sorular",
      performance: "Performans Görevleri",
      evidence: "Diğer Kanıtlar",
      tymm: "TYMM Öğrenme-Öğretme Uygulamaları",
    };
    return titles[type as keyof typeof titles] || type;
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      purple: {
        bg: "from-purple-500 to-pink-600",
        light: "bg-purple-50/50 dark:bg-purple-900/10",
        border: "border-purple-200/30 dark:border-purple-700/30",
        badge:
          "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
      },
      amber: {
        bg: "from-amber-500 to-orange-600",
        light: "bg-amber-50/50 dark:bg-amber-900/10",
        border: "border-amber-200/30 dark:border-amber-700/30",
        badge:
          "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300",
      },
      blue: {
        bg: "from-blue-500 to-indigo-600",
        light: "bg-blue-50/50 dark:bg-blue-900/10",
        border: "border-blue-200/30 dark:border-blue-700/30",
        badge:
          "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
      },
      emerald: {
        bg: "from-emerald-500 to-green-600",
        light: "bg-emerald-50/50 dark:bg-emerald-900/10",
        border: "border-emerald-200/30 dark:border-emerald-700/30",
        badge:
          "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300",
      },
      teal: {
        bg: "from-teal-500 to-cyan-600",
        light: "bg-teal-50/50 dark:bg-teal-900/10",
        border: "border-teal-200/30 dark:border-teal-700/30",
        badge:
          "bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300",
      },
      indigo: {
        bg: "from-indigo-500 to-purple-600",
        light: "bg-indigo-50/50 dark:bg-indigo-900/10",
        border: "border-indigo-200/30 dark:border-indigo-700/30",
        badge:
          "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300",
      },
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getCourseName = () => {
    const courseInfo =
      CourseData[formData.courseType as keyof typeof CourseData];
    if (courseInfo) {
      return t(`courseType.${courseInfo.name(formData.grade)}`);
    }
    return formData.courseType;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header - Similar to LessonPlanOutput */}
        <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6">
          <div className="flex items-center justify-between">
            <div>
              <Text
                variant="h2"
                className="text-2xl font-bold text-gray-900 dark:text-white"
              >
                UbD Ders Akışı Planlayıcısı
              </Text>
              <Text
                variant="p"
                size="sm"
                className="mt-1 text-gray-500 dark:text-gray-400"
              >
                {formData.grade}. Sınıf • {getCourseName()} • {cards.length}{" "}
                Etkinlik Kartı
              </Text>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <Eye className="w-4 h-4" />
                <span>Plan Görünümü</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Card-based layout similar to ClassicTeachingFlow */}
        <div className="p-6 space-y-8">
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formData.ubdTransferGoals.length}
              </div>
              <div className="text-sm text-purple-600 dark:text-purple-400">
                Transfer Hedefleri
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {formData.ubdUnderstandingGoals.length}
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-400">
                Anlama Hedefleri
              </div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {formData.ubdPerformanceTasks.length}
              </div>
              <div className="text-sm text-emerald-600 dark:text-emerald-400">
                Performans Görevleri
              </div>
            </div>
            <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                {formData.ubdOtherEvidence.length}
              </div>
              <div className="text-sm text-teal-600 dark:text-teal-400">
                Diğer Kanıtlar
              </div>
            </div>
          </div>

          {/* Cards Groups */}
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <Text variant="h3" className="text-lg font-semibold">
                Ders Akışı Bileşenleri
              </Text>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {cards.length} bileşen • Düzenlenebilir içerik
              </div>
            </div>

            {Object.entries(groupedCards).map(([type, typeCards]) => {
              const firstCard = typeCards[0];
              const colors = getColorClasses(firstCard.color);
              const IconComponent = firstCard.icon;

              return (
                <motion.div
                  key={type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Group Header */}
                  <div className={`bg-gradient-to-r ${colors.bg} p-4`}>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <Text variant="h3" className="text-white font-semibold">
                          {getGroupTitle(type)}
                        </Text>
                        <Text variant="p" size="sm" className="text-white/80">
                          {typeCards.length} bileşen
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Group Content */}
                  <div className="p-4 space-y-4">
                    {typeCards.map((card, index) => (
                      <div
                        key={card.id}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-200"
                      >
                        <div className="p-4">
                          <div className="flex items-start space-x-3">
                            {/* Code Badge */}
                            <div
                              className={`flex-shrink-0 w-8 h-8 bg-gradient-to-r ${colors.bg} rounded-lg flex items-center justify-center`}
                            >
                              <Text
                                variant="span"
                                className="text-white font-bold text-sm"
                              >
                                {card.code}
                              </Text>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {editingCard === card.id ? (
                                /* Edit Mode */
                                <div className="space-y-3">
                                  <textarea
                                    value={editContent}
                                    onChange={(e) =>
                                      setEditContent(e.target.value)
                                    }
                                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    rows={4}
                                    placeholder="İçeriği düzenleyin..."
                                  />
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      onClick={handleSaveEdit}
                                      size="sm"
                                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Kaydet
                                    </Button>
                                    <Button
                                      onClick={handleCancelEdit}
                                      size="sm"
                                      variant="outline"
                                      className="px-4 py-2"
                                    >
                                      İptal
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                /* View Mode */
                                <div className="space-y-2">
                                  <Text
                                    variant="p"
                                    size="sm"
                                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                                  >
                                    {card.content}
                                  </Text>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <div
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${colors.badge}`}
                                      >
                                        {card.type === "transfer" && "Transfer"}
                                        {card.type === "understanding" &&
                                          "Anlama"}
                                        {card.type === "questions" && "Soru"}
                                        {card.type === "performance" &&
                                          "Performans"}
                                        {card.type === "evidence" && "Kanıt"}
                                        {card.type === "tymm" && "TYMM"}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <button
                                        onClick={() => handleStartEdit(card)}
                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                                        title="Düzenle"
                                      >
                                        <Edit3 className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                                      </button>
                                      <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors">
                                        <ThumbsUp className="w-4 h-4 text-gray-400 hover:text-green-500" />
                                      </button>
                                      <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors">
                                        <ThumbsDown className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-6">
          <div className="flex items-center justify-between space-x-3">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Plan hazır! Artık AI ile detaylı ders akışı oluşturabilirsiniz.
            </div>
            <Button
              onClick={onCreateLessonFlow}
              disabled={isLoading || cards.length < 3 || !formData.planId}
              className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Ders Akışı Oluştur
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UbdPlanSummaryStep;
